import {
  Module,
  VuexModule,
  Mutation,
  config,
  Action
} from "vuex-module-decorators";
import {
  AvailableDependencyFiles,
  BuilderElement,
  BuilderElementCategory,
  BuilderElements,
  RequiredPlugin,
  ElementsExternalAttrsDepsAndActions,
  MacrosData
} from "@/types/elements";
import { Breakpoint } from "@/types/breakpoints";
import { DynamicData, DynamicField } from "@/types/dynamic-data";
import { Font } from "@/types/fonts";
import { GlobalSettingsControlsAndTemplate } from "@/types/global-and-document-settings";
import { appendRootElement } from "@/lib/elements/root";
import { TemplatePreview, TemplatePreviewableItem } from "@/types/templates";
import { Control } from "@/types/controls";
import { IconSet } from "@/types/icons";
import {
  BreakdanceNeededData,
  BreakdanceDocumentSpecificNeededUrls
} from "@/types/config";
import { ElementPreset } from "@/types/presets";
import { GlobalBlock } from "@/types/global-blocks";
import { Condition } from "@/types/conditions";
import { Preferences } from "@/types/preferences";
import Vue from "vue";
import defensiveCopy from "@/util/defensive-copy";
import { DocumentMeta } from "@/types/document";
import { Permission } from "@/types/permission";
import { MISSING_ELEMENT_SLUG } from "@/lib/elements/constants";
import { GlobalMediaSize } from "@/types/media";
import { PostType } from "@/types/post-types";
import {
  Ai,
  ConfigDataLoadedFromWordPress,
  Environment,
  Interactions,
  InvalidPostData
} from "@/types/load";
import { documentStore, uiStore } from "@/store/store";
import { DesignLibraryData } from "@/types/design-library";
import {
  getBreakdanceBlockPostType,
  getBreakdanceFooterPostType,
  getBreakdanceHeaderPostType
} from "@/lib/constants/post-types";
import { LoadedOxySelectorData } from "@/oxygen/classes/lib/types";
import { LoadedVariablesData } from "@/oxygen/variables/lib/types";

config.rawError = true;
@Module({ name: "config", namespaced: true })
export default class ConfigModule extends VuexModule {
  builderMode: "breakdance" | "oxygen" | null = null;

  subscriptionMode: ConfigDataLoadedFromWordPress["subscriptionMode"] | null =
    null;

  neededData: null | BreakdanceNeededData = null;

  environment: null | Environment = null;

  documentSpecificNeededUrls: null | BreakdanceDocumentSpecificNeededUrls =
    null;

  invalidPostData: null | InvalidPostData = null;
  elements: BuilderElements = {};
  elementsLoadingState: "success" | "loading" | "failed" = "loading";
  elementsExternalAttrsDepsAndActions: ElementsExternalAttrsDepsAndActions = {};
  dynamicFields: DynamicField[] = [];
  fonts: Font[] = [];
  iconSets: IconSet[] = [];

  templatePreview: TemplatePreview = {
    previewableItems: false,
    lastPreviewedItem: false
  };
  previewedItem: TemplatePreviewableItem | null = null;

  selectorControls: Control[] = [];

  classTemplate = "";
  globalSettingsControlsAndTemplate: GlobalSettingsControlsAndTemplate = {
    template: "",
    controls: [],
    propertyPathsToWhitelistInFlatProps: []
  };
  availableDocuments: DocumentMeta[] = [];
  presetsForElements: ElementPreset[] = [];
  globalBlocks: GlobalBlock[] = [];
  conditions: Condition[] = [];
  postTypes: PostType[] = [];
  builderElementCategories: BuilderElementCategory[] = [];
  twigMacros: MacrosData = [];
  twigMacrosHash: ConfigDataLoadedFromWordPress["twigMacrosHash"] | null = null;
  availableWordpressPlugins: RequiredPlugin[] = [];
  leavingIframeOnPurpose = false;
  isElementStudioPowerUser = false;
  oxySelectorsControlsAndTemplate: {
    template: string;
    propertyPathsToWhitelistInFlatProps: string[];
  } = {
    template: "",
    propertyPathsToWhitelistInFlatProps: []
  };
  variablesControlsAndTemplate: {
    template: string;
    propertyPathsToWhitelistInFlatProps: string[];
  } = {
    template: "",
    propertyPathsToWhitelistInFlatProps: []
  };

  preferences: Preferences = {
    customFonts: [],
    customBreakpoints: []
  };

  builtinBreakpoints: Breakpoint[] = [];

  designLibrary: DesignLibraryData = {
    designProviders: [],
    globalSettingsLastImportedFromUrl: null,
    designPresetsLastImportedFromUrl: null,
    variablesLastImportedFromUrl: null,
    oxySelectorsLastImportedFromUrl: null,
    fullSiteLastImportedFromUrl: null,
    isGlobalSettingsEnabled: true,
    isDesignPresetsEnabled: true,
    isVariablesEnabled: false,
    isOxySelectorsEnabled: false,
    isPopupsEnabled: true
  };

  availableMediaSizes: GlobalMediaSize[] = [];

  availablePermissions: Permission[] = [];
  permission: Permission | null = null;

  ai: Ai | null = null;

  interactions: Interactions | null = null;

  dynamicDataCache: { data: DynamicData; loading: boolean } = {
    data: {},
    loading: true
  };

  localDependenciesFiles: AvailableDependencyFiles[] = [];

  experiments: string[] = [];

  get breakpointIds(): Breakpoint["id"][] {
    return this.breakpoints.map(breakpoint => breakpoint.id);
  }

  get breakpoints(): Breakpoint[] {
    return [...this.builtinBreakpoints, ...this.preferences.customBreakpoints];
  }

  @Mutation
  setElementStudioPowerUser(enableElementStudioPowerUser: boolean) {
    this.isElementStudioPowerUser = enableElementStudioPowerUser;
  }

  @Mutation
  setSubscriptionMode(
    subscriptionMode: ConfigDataLoadedFromWordPress["subscriptionMode"]
  ) {
    this.subscriptionMode = subscriptionMode;
  }

  @Mutation
  setBuilderMode(builderMode: ConfigDataLoadedFromWordPress["builderMode"]) {
    this.builderMode = builderMode;
  }

  @Mutation
  setAi(ai: Ai) {
    this.ai = ai;
  }

  @Mutation
  setInteractions(interactions: Interactions) {
    this.interactions = interactions;
  }

  @Mutation
  setLeavingIframeOnPurpose(onPurpose: boolean) {
    this.leavingIframeOnPurpose = onPurpose;
  }

  @Mutation
  setGlobalBlocks(globalBlocks: GlobalBlock[]) {
    this.globalBlocks = globalBlocks;
  }

  @Mutation
  setTwigMacros(twigMacros: MacrosData) {
    this.twigMacros = twigMacros;
  }

  @Mutation
  setTwigMacrosHash(hash: ConfigDataLoadedFromWordPress["twigMacrosHash"]) {
    this.twigMacrosHash = hash;
  }

  @Mutation
  setAvailableWordpressPlugins(availableWordpressPlugins: RequiredPlugin[]) {
    this.availableWordpressPlugins = availableWordpressPlugins;
  }

  @Mutation
  setDesignLibrary(designLibraryData: DesignLibraryData) {
    this.designLibrary = designLibraryData;
  }

  @Mutation
  setAvailablePermissions(permissions: Permission[]) {
    this.availablePermissions = permissions;
  }

  @Mutation
  setAvailableMediaSizes(sizes: GlobalMediaSize[]) {
    this.availableMediaSizes = sizes;
  }

  @Mutation
  setPermission(permission: Permission) {
    this.permission = permission;
  }

  @Mutation
  setPreferences(newPrefs: Preferences) {
    this.preferences = newPrefs;
  }

  @Mutation
  setElementCategories(builderElementCategories: BuilderElementCategory[]) {
    this.builderElementCategories = builderElementCategories;
  }

  @Mutation
  setPresetsForElements(presetsForElements: ElementPreset[]) {
    this.presetsForElements = presetsForElements;
  }

  @Mutation
  setBuiltinBreakpoints(breakpoints: Breakpoint[]) {
    this.builtinBreakpoints = breakpoints;
  }

  @Mutation
  setAvailableDocuments(availableDocuments: DocumentMeta[]) {
    this.availableDocuments = availableDocuments;
  }

  @Mutation
  setSelectorControls(controls: Control[]) {
    this.selectorControls = controls;
  }

  @Mutation
  setClassTemplate(template: string) {
    this.classTemplate = template;
  }

  @Mutation
  setOxySelectorsControlsAndTemplate(data: LoadedOxySelectorData) {
    this.oxySelectorsControlsAndTemplate = {
      template: data.template,
      propertyPathsToWhitelistInFlatProps:
        data.propertyPathsToWhitelistInFlatProps
    };
  }

  @Mutation
  setVariablesControlsAndTemplate(data: LoadedVariablesData) {
    this.variablesControlsAndTemplate = {
      template: data.template,
      propertyPathsToWhitelistInFlatProps:
        data.propertyPathsToWhitelistInFlatProps
    };
  }

  @Action
  loadTemplatePreviewData(templatePreview: TemplatePreview) {
    this.setTemplatePreview(templatePreview);

    const templatePreviewableItems =
      templatePreview && templatePreview.previewableItems;

    if (
      !this.previewedItem &&
      templatePreviewableItems &&
      templatePreviewableItems.length > 0
    ) {
      let previewableItem = templatePreviewableItems[0];

      if (templatePreview && templatePreview.lastPreviewedItem) {
        // Why is TS not removing the "false" correctly inside "find"? I'm forced to re-assign
        const lastPreviewedItem = templatePreview.lastPreviewedItem;

        // lastPreviewedItem is added in PHP's `getTemplatePreviewableItems`, so that it's always present on the initial load
        const storedPreviewableItem = templatePreviewableItems.find(
          item => item.url === lastPreviewedItem.url
        );

        if (storedPreviewableItem) {
          previewableItem = storedPreviewableItem;
        }
      }

      if (previewableItem) {
        this.setPreviewedItem(previewableItem);
      }
    }
  }

  @Mutation
  setTemplatePreview(templatePreview: TemplatePreview) {
    this.templatePreview = defensiveCopy(templatePreview);
  }

  get previewOnlyDynamicData() {
    return [
      getBreakdanceBlockPostType(),
      getBreakdanceHeaderPostType(),
      getBreakdanceFooterPostType(),
      "breakdance_popup",
      "breakdance_acf_block"
    ].includes(documentStore.document.documentMeta.postType);
  }

  get isExperimentEnabled() {
    return (experimentName: string) =>
      this.experiments.includes(experimentName);
  }

  @Action
  setPreviewedItem(item: TemplatePreviewableItem | null) {
    this.setPreviewedItemMutation(item);

    if (this.previewOnlyDynamicData) {
      // We still want a full reload, but we're not updating the iframe/canvas
      uiStore.forceUpdateEntireCanvas();
    } else if (item) {
      this.setIframeUrl(item.url);
    }
  }

  @Mutation
  setPreviewedItemMutation(item: TemplatePreviewableItem | null) {
    this.previewedItem = item;
  }

  @Action
  setIframeUrl(newUrl: string) {
    if (!this.documentSpecificNeededUrls) return;

    if (newUrl !== this.documentSpecificNeededUrls.iframeUrl) {
      uiStore.setCanvasState("loading");
      this.setLeavingIframeOnPurpose(true);
      this.setIframeUrlMutation(newUrl);
    }
  }

  @Mutation
  setIframeUrlMutation(newUrl: string) {
    if (!this.documentSpecificNeededUrls) return;
    this.documentSpecificNeededUrls.iframeUrl = newUrl;
  }

  @Mutation
  setInvalidPostData(newInvalidPostData: InvalidPostData) {
    this.invalidPostData = newInvalidPostData;
  }

  get element(): (elementSlug: BuilderElement["slug"]) => BuilderElement {
    const MISSING_ELEMENT = this.elements[
      MISSING_ELEMENT_SLUG
    ] as unknown as BuilderElement;

    return (elementSlug: BuilderElement["slug"]): BuilderElement => {
      const element = this.elements[elementSlug];
      if (!element) {
        console.warn(`Element ${elementSlug} is missing.`);
      }
      return element ?? MISSING_ELEMENT;
    };
  }

  @Mutation
  setGlobalSettingsControlsAndTemplate(
    globalSettingsControlsAndTemplate: GlobalSettingsControlsAndTemplate
  ) {
    this.globalSettingsControlsAndTemplate = globalSettingsControlsAndTemplate;
  }

  @Mutation
  setEnvironment(env: Environment) {
    this.environment = env;
  }

  @Action
  setElementsAndAppendRootElement(elements: BuilderElement[]) {
    const newElements: BuilderElements = {};

    elements.forEach(element => {
      newElements[element.slug] = element;
    });

    this.setElementsMutation(appendRootElement(newElements));
  }

  @Mutation
  setElementsMutation(elements: BuilderElements) {
    this.elements = elements;
  }

  @Mutation
  setExperiments(experiments: string[]) {
    this.experiments = experiments;
  }

  @Mutation
  addElements(elements: BuilderElement[]) {
    elements.forEach(element => {
      Vue.set(this.elements, element.slug, element);
    });
  }

  @Mutation
  addNonExistentElementsNonReactively(elements: BuilderElement[]) {
    elements.forEach(element => {
      // Only add non-existent elements to avoid redundant tree node re-rendering
      if (!(element.slug in this.elements)) {
        this.elements[element.slug] = element;
      }
    });
  }

  @Mutation
  setElementsExternalAttrsDepsAndActions(
    newElementsExternalAttrsDepsAndActions: ElementsExternalAttrsDepsAndActions
  ) {
    this.elementsExternalAttrsDepsAndActions =
      newElementsExternalAttrsDepsAndActions;
  }

  @Mutation
  updateElement(newElement: BuilderElement) {
    if (this.elements[newElement.slug]) {
      this.elements[newElement.slug] = newElement;
    } else {
      Vue.set(this.elements, newElement.slug, newElement);
    }
  }

  @Mutation
  deleteElement(elementSlug: string) {
    Vue.delete(this.elements, elementSlug);
  }

  @Mutation
  setNeededUrls(neededData: BreakdanceNeededData) {
    this.neededData = neededData;
  }

  @Mutation
  setDocumentSpecificNeededUrls(
    neededData: BreakdanceDocumentSpecificNeededUrls
  ) {
    this.documentSpecificNeededUrls = neededData;
  }

  @Mutation
  setDynamicFields(fields: DynamicField[]) {
    this.dynamicFields = fields;
  }

  @Mutation
  setConditions(conditions: Condition[]) {
    this.conditions = conditions;
  }

  @Mutation
  setPostTypes(postTypes: PostType[]) {
    this.postTypes = postTypes;
  }

  @Mutation
  setFonts(fonts: Font[]) {
    this.fonts = fonts;
  }

  @Mutation
  setIconSets(iconSets: IconSet[]) {
    this.iconSets = iconSets;
  }

  @Mutation
  updateDynamicDataCache(newDynamicData: DynamicData) {
    this.dynamicDataCache = {
      data: defensiveCopy(newDynamicData),
      loading: false
    };
  }

  @Mutation
  setNewDynamicDataCacheEntries(newDynamicData: DynamicData) {
    for (const [key, value] of Object.entries(newDynamicData)) {
      Vue.set(this.dynamicDataCache.data, key, defensiveCopy(value));
    }

    this.dynamicDataCache.loading = false;
  }

  @Mutation
  flushDynamicDataCache() {
    this.dynamicDataCache = { data: {}, loading: true };
  }

  @Mutation
  setLocalDependenciesFiles(
    localDependenciesFiles: AvailableDependencyFiles[]
  ) {
    this.localDependenciesFiles = localDependenciesFiles;
  }

  @Mutation
  setElementsLoadingState(state: ConfigModule["elementsLoadingState"]) {
    this.elementsLoadingState = state;
  }
}
